import React from 'react';
import { Card, CardContent, Typography, Box, useTheme, Chip } from '@mui/material';
import { TopVideo } from '../../../app/network/api/dashboard';

interface TopVideosCardProps {
    videos: TopVideo[];
    loading?: boolean;
}

const TopVideosCard: React.FC<TopVideosCardProps> = ({ videos, loading = false }) => {
    const theme = useTheme();

    const getRankColor = (rank: number) => {
        switch (rank) {
            case 1:
                return theme.palette.warning.main; // Gold
            case 2:
                return theme.palette.grey[400]; // Silver
            case 3:
                return '#CD7F32'; // Bronze
            default:
                return theme.palette.primary.main;
        }
    };

    if (loading) {
        return (
            <Card>
                <CardContent>
                    <Typography variant="h6" gutterBottom>
                        인기 영상 TOP 3
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                        <Typography color="text.secondary">로딩 중...</Typography>
                    </Box>
                </CardContent>
            </Card>
        );
    }

    if (!videos || videos.length === 0) {
        return (
            <Card>
                <CardContent>
                    <Typography variant="h6" gutterBottom>
                        인기 영상 TOP 3
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                        <Typography color="text.secondary">데이터 없음</Typography>
                    </Box>
                </CardContent>
            </Card>
        );
    }

    return (
        <Card>
            <CardContent>
                <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
                    완료 영상 TOP 3
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {videos.slice(0, 3).map((video, index) => {
                        const rank = index + 1;
                        return (
                            <Box
                                key={video.seqId}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 2,
                                    p: 2,
                                    borderRadius: 2,
                                    bgcolor: 'rgba(255, 255, 255, 0.05)',
                                    transition: 'all 0.2s',
                                    '&:hover': {
                                        bgcolor: 'rgba(255, 255, 255, 0.08)',
                                        transform: 'translateX(4px)',
                                    },
                                }}
                            >
                                {/* Rank Badge */}
                                <Box
                                    sx={{
                                        minWidth: 48,
                                        height: 48,
                                        borderRadius: '50%',
                                        bgcolor: getRankColor(rank),
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontWeight: 'bold',
                                        fontSize: '1.25rem',
                                        color: '#fff',
                                        boxShadow: `0 2px 8px ${getRankColor(rank)}40`,
                                    }}
                                >
                                    #{rank}
                                </Box>

                                {/* Video Info */}
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography
                                        variant="subtitle1"
                                        sx={{
                                            fontWeight: 600,
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                        }}
                                    >
                                        {video.title}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {video.completionCount}회 완료
                                    </Typography>
                                </Box>

                                {/* Completion Count Chip */}
                                <Chip
                                    label={`${video.completionCount}회`}
                                    color="success"
                                    size="small"
                                    sx={{
                                        fontWeight: 600,
                                        minWidth: 70,
                                    }}
                                />
                            </Box>
                        );
                    })}
                </Box>
            </CardContent>
        </Card>
    );
};

export default TopVideosCard;
